<template>
  <ScaleLoader v-if="loading" />
  <div v-else>
    <ExportButtons
      :csv="exportGridsToExcel"
      :pdf="exportGridsToPdf"
      :xlsx="exportGridsToExcel"
    />
    <div class="vehicle-report-grid-card">
      <div class="col-sm-12">
        <Card
          :badges="3"
          :info-badge-description="[cardStore[0], cardStore[1], cardStore[2]]"
          :info-badge-title="[$t('components.reports.eventsList.totalDistance'), $t('components.reports.eventsList.drivingTime'), $t('components.reports.eventsList.breakTime')]"
          :status="$t('components.reports.eventsList.eventsList')"
          icon="bi bi-graph-down"
        />
      </div>
    </div>
    <div class="row">
      <DxDataGrid
        :ref="hiddenDataGridRef"
        :data-source="store.details"
        style="display: none;"
      >
        <DxColumn :caption="$t('components.reports.eventsList.eventsList')">
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.totalDistance')"
            :filter-operations="allowedOperations"
            data-field="full_distance"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.drivingTime')"
            :filter-operations="allowedOperations"
            data-field="drive_time"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.breakTime')"
            :filter-operations="allowedOperations"
            data-field="rest_time"
          />
        </DxColumn>
      </DxDataGrid>
      <DxDataGrid
        :ref="dataGridRef"
        :allow-column-reordering="true"
        :allow-column-resizing="true"
        :column-auto-width="true"
        :column-hiding-enabled="true"
        class="vehicle-report-grid"
        :data-source="eventsStore"
        :hover-state-enabled="true"
        :remote-operations="remoteOperations"
        :row-alternation-enabled="true"
        :show-borders="true"
        :show-column-lines="true"
        :show-row-lines="false"
      >
        <DxHeaderFilter :visible="false" />
        <DxPaging
          :page-index="0"
          :page-size="25"
        /> <!-- Shows the second page -->
        <DxPager
          :allowed-page-sizes="[10, 20, 25]"
          :show-info="true"
          :show-navigation-buttons="true"
          :show-page-size-selector="true"
        />
        <DxFilterRow :visible="true" />
        <DxColumnFixing :enabled="true" />
        <DxColumn :caption="$t('components.reports.eventsList.eventsList')">
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.start')"
            :filter-operations="allowedOperations"
            :customize-text="formatTimestampCell"
            data-field="start_timestamp"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.stop')"
            :filter-operations="allowedOperations"
            :customize-text="formatTimestampCell"
            data-field="stop_timestamp"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.state')"
            :filter-operations="allowedOperations"
            cell-template="ignition-cell-template"
            data-field="ignition"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.time')"
            :filter-operations="allowedOperations"
            :customize-text="formatSecondsCell"
            data-field="time"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.mapDistance')"
            :filter-operations="allowedOperations"
            data-field="distance.map_distance"
          />
          <DxColumn
            :allow-filtering="true"
            :allow-header-filtering="false"
            :caption="$t('components.reports.eventsList.canDistance')"
            :filter-operations="allowedOperations"
            data-field="distance.can_distance"
          />
        </DxColumn>
        <template #ignition-cell-template="{ data }">
          <span
            class="report-status-pill"
            :class="data.data.ignition ? 'report-status-pill--active' : 'report-status-pill--inactive'"
          >
            <span class="report-status-dot" />
            {{ data.data.ignition ? $t('common.on') : $t('common.off') }}
          </span>
        </template>
      </DxDataGrid>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import Card from '@/components/extras/Card.vue'
import { getEventsListReport } from '@/crud/getters'
import {
  DxColumn,
  DxColumnFixing,
  DxDataGrid,
  DxFilterRow,
  DxHeaderFilter,
  DxPager,
  DxPaging
} from 'devextreme-vue/data-grid'
import { Workbook } from 'exceljs'
import { saveAs } from 'file-saver'
import { exportDataGrid } from 'devextreme/excel_exporter'
import { getHumanDateTime, minutesToHoursMinutes, secondsToHoursMinutes } from '@/utils/tools'
import ExportButtons from '@/components/content/_partials/ExportButtons.vue'
import { jsPDF } from 'jspdf'
import 'jspdf-autotable'
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter'
import { importFont } from '@/utils/jspdf/importFont'
import ScaleLoader from 'vue-spinner/src/ScaleLoader.vue'
import 'jspdf-autotable'
import CustomStore from 'devextreme/data/custom_store'
import { buildGridLoadParams, normalizeGridResponse } from '@/services/reports/grid-store'

export default defineComponent({
  name: 'Events',
  components: {
    ExportButtons,
    DxColumn,
    DxColumnFixing,
    DxDataGrid,
    DxFilterRow,
    DxHeaderFilter,
    DxPager,
    DxPaging,
    Card,
    ScaleLoader
  },
  props: {
    imei: BigInt as unknown as PropType<bigint>,
    dateFrom: String,
    dateTo: String,
    vehicle: Object,
    reportType: String
  },
  data(): {
    store: any,
    eventsStore: any,
    cardStore: Array<any>,
    allowedOperations: Array<string>,
    remoteOperations: boolean,
    dataGridRef: string,
    hiddenDataGridRef: string,
    loading: boolean
  } {
    return {
      store: [],
      eventsStore: null,
      cardStore: [],
      allowedOperations: ['contains', '=', 'notcontains', '!=', '>', '>=', '<', '<='],
      remoteOperations: true,
      dataGridRef: 'eventsGrid',
      hiddenDataGridRef: 'hiddenEventsGrid',
      loading: true
    }
  },
  computed: {
    eventsListGridInstance() {
      const self: any = this
      return self.$refs[this.dataGridRef].instance
    },
    hiddenEventsListGridInstance() {
      const self: any = this
      return self.$refs[this.hiddenDataGridRef].instance
    }
  },
  methods: {
    exportGridsToExcel(extension: string) {
      const self: any = this
      const workbook = new Workbook()

      const sheet = workbook
          .addWorksheet(self.$t('components.reports.tabs.reports'))

      const secondSheet = workbook
          .addWorksheet(self.$t('components.reports.tabs.reports') + ' #2')

      exportDataGrid({
        worksheet: sheet,
        component: self.hiddenEventsListGridInstance
      })
          .then(() => exportDataGrid({
            worksheet: secondSheet,
            component: self.eventsListGridInstance
          }))
          .then(() => {
            if (extension == 'csv')
              workbook.csv.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                    `Events_report_${ self.imei }.csv`)
              })
            else if (extension == 'xlsx')
              workbook.xlsx.writeBuffer().then((buffer) => {
                saveAs(new Blob([buffer], { type: 'application/octet-stream' }),
                    `Events_report_${ self.imei }.xlsx`)
              })
          })
    },
    exportGridsToPdf() {
      const doc = new jsPDF()
      importFont(doc)

      // exportDataGridToPdf({
      //   jsPDFDocument: doc,
      //   component: this.hiddenEventsListGridInstance,
      //   // autoTableOptions: {
      //   //   styles: {
      //   //     font: 'Roboto'
      //   //   }
      //   // }
      // })
         exportDataGridToPdf({
            jsPDFDocument: doc,
            component: this.eventsListGridInstance,
            // autoTableOptions: {
            //   styles: {
            //     font: 'Roboto'
            //   }
            // }
          })
          .then(() => {
            doc.save(`Events_report_${this.imei}.pdf`)
          })
    },
    formatTimestampCell(cellInfo: any): string {
      return cellInfo.value ? getHumanDateTime(cellInfo.value) : ''
    },
    formatSecondsCell(cellInfo: any): string {
      return secondsToHoursMinutes(cellInfo.value || 0)
    },
    initEventsStore(): void {
      const self: any = this
      self.eventsStore = new CustomStore({
        load(loadOptions: any) {
          return getEventsListReport(
              self.imei,
              self.dateFrom,
              self.dateTo,
              buildGridLoadParams(loadOptions)
          ).then((res) => {
            self.store = res.data
            self.prepareSummary(res.data)
            return normalizeGridResponse(res.data.events_list)
          }).catch(() => {
            self.$swal.fire({
              icon: 'warning',
              title: 'Oops...',
              text: self.$t('components.error')
            })
            return {data: [], totalCount: 0}
          })
        }
      })
    },
    prepareSummary(data: any): void {
      if (this.cardStore.length || !data?.details?.[0]) return

      const details = data.details[0]
      details.drive_time = minutesToHoursMinutes(details.drive_time)
      details.rest_time = minutesToHoursMinutes(details.rest_time)

      const mapDistanceLabel = this.$t('components.reports.eventsList.mapDistance').replace(' [km]', '')
      const canDistanceLabel = this.$t('components.reports.eventsList.canDistance').replace(' [km]', '')
      const distanceDisplay = `${mapDistanceLabel}: ${details.full_distance.map_distance} km\n${canDistanceLabel}: ${details.full_distance.can_distance} km`

      this.cardStore.push(distanceDisplay, details.drive_time, details.rest_time)
    }
  },
  mounted() {
    if (this.imei && this.dateFrom && this.dateTo) {
      this.initEventsStore()
      this.loading = false
    }
  }
})
</script>
<style lang="scss" scoped src="@/assets/scss/report-grid.scss"></style>
